import React, { useEffect, useRef } from 'react';
import { Chart as ChartJS, registerables } from 'chart.js';

ChartJS.register(...registerables);

const DepartmentEngagementChart = ({ data = [] }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const isHoveringRef = useRef(false);

    // Default data if none provided
    const defaultData = [
        { department: 'CSE', engagement: 50 },
        { department: 'ME', engagement: 100 },
        { department: 'EE', engagement: 320 },
        { department: 'ECE', engagement: 550 },
        { department: 'Civil', engagement: 450 },
        { department: 'Others', engagement: 480 },
    ];

    const chartData = data.length > 0 ? data : defaultData;
    const labels = chartData.map(item => item.department);
    const values = chartData.map(item => item.engagement);
    const maxValue = Math.max(...values, 10);

    useEffect(() => {
        if (chartRef.current) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            const ctx = chartRef.current.getContext('2d');

            // Calculate appropriate step size based on max value
            const stepSize = Math.ceil(maxValue / 4 / 50) * 50 || 50;
            const yMax = Math.ceil(maxValue * 1.2 / stepSize) * stepSize;

            // Custom plugin to show labels only on hover
            const hoverLabelsPlugin = {
                id: 'hoverLabelsPlugin',
                afterDatasetsDraw(chart) {
                    // Only draw labels if we're hovering
                    if (!isHoveringRef.current) return;

                    const { ctx } = chart;
                    const meta = chart.getDatasetMeta(0);

                    ctx.save();
                    meta.data.forEach((bar, index) => {
                        const value = chart.data.datasets[0].data[index];
                        const x = bar.x;
                        const y = bar.y;

                        // Draw the label above the bar
                        ctx.fillStyle = '#1142A4';
                        ctx.font = '600 12px "Inter", sans-serif';
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'bottom';
                        ctx.fillText(value, x, y - 5);
                    });
                    ctx.restore();
                }
            };

            chartInstance.current = new ChartJS(ctx, {
                type: 'bar',
                data: {
                    labels: labels,
                    datasets: [
                        {
                            label: 'Engagement',
                            data: values,
                            backgroundColor: '#D1E0FF',
                            borderColor: '#1142A4',
                            borderWidth: 1,
                            borderRadius: 4,
                            barThickness: labels.length > 8 ? 20 : 32,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    onHover: (event, activeElements, chart) => {
                        isHoveringRef.current = true;
                        chart.update('none');
                    },
                    plugins: {
                        legend: { display: false },
                        datalabels: { display: false },
                        tooltip: {
                            enabled: true,
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            titleColor: '#333',
                            bodyColor: '#333',
                            borderColor: '#1142A4',
                            borderWidth: 1,
                            padding: 10,
                            displayColors: false,
                            callbacks: {
                                label: (context) => `Engagement: ${context.raw}`
                            }
                        }
                    },
                    scales: {
                        x: {
                            grid: { display: false },
                            ticks: {
                                color: '#666666',
                                font: { size: labels.length > 10 ? 10 : 12, family: '"Inter", sans-serif' },
                                maxRotation: labels.length > 8 ? 45 : 0,
                                minRotation: labels.length > 8 ? 45 : 0,
                            },
                            border: {
                                display: true,
                                color: '#e0e0e0',
                            },
                        },
                        y: {
                            min: 0,
                            max: yMax,
                            ticks: {
                                stepSize: stepSize,
                                color: '#666666',
                                font: { size: 12, family: '"Inter", sans-serif' },
                            },
                            grid: {
                                color: '#f0f0f0',
                                drawBorder: false,
                            },
                            border: {
                                display: false,
                            },
                        },
                    },
                },
                plugins: [hoverLabelsPlugin]
            });

            // Add mouse leave listener to hide labels when mouse leaves
            const canvas = chartRef.current;
            const handleMouseLeave = () => {
                isHoveringRef.current = false;
                if (chartInstance.current) {
                    chartInstance.current.update('none');
                }
            };
            canvas.addEventListener('mouseleave', handleMouseLeave);

            return () => {
                canvas.removeEventListener('mouseleave', handleMouseLeave);
                if (chartInstance.current) chartInstance.current.destroy();
            };
        }
    }, [data, labels, values, maxValue]);

    return <canvas ref={chartRef} style={{ width: '100%', height: '100%' }} />;
};

export default DepartmentEngagementChart;
